// ─────────────────────────────────────────────────────────────────────────────
// utils/payfixPrinterId.ts — die Drucker-Kennung der Kasse (T-M11H.3)
//
// REQ-DM-035 (Feldvertrag `printers.payfixPrinterId`), REQ-DM-036 (die
// Verwaltungsoberflaeche fuehrt das Feld), REQ-DM-060 (beide Printer-Typen).
//
// WORUM ES GEHT — der Anlass ist eine Produktivstoerung, nicht eine Aufraeumung:
// `payfixPrinterId` ist der EINZIGE Aufloesungsschluessel des Alt-Eingangs
// (`payfixlegacywebhook` -> `resolveLegacyPrinter`). Die Verwaltungsoberflaeche
// hat das Feld nie geschrieben, sondern das Grundgeruest-Feld `number` — jeder
// so angelegte Drucker war fuer die Kasse nicht vorhanden (HTTP 404).
//
// DER ENTSCHEID (REQ-DM-036): EIN sichtbares Eingabefeld. Sein Wert wird nach
// `payfixPrinterId` geschrieben (kanonische Quelle) und auf `number` GESPIEGELT
// (Rueckwaertsvertraeglichkeit; `number` wird von keiner Cloud Function, keinem
// Druckserver und keiner Rule ausgewertet und ist Aufraeum-Kandidat, Klasse
// OP-59). GELESEN wird ueberall `payfixPrinterId ?? number`.
//
// ZEICHENKETTE, NICHT ZAHL — das ist kein Detail, sondern der Kern des Fehlers:
// Firestore vergleicht TYPSTRENG. Eine Gleichheitsabfrage auf `payfixPrinterId`
// mit der Zeichenkette '3324' findet ein Dokument mit dem ZAHLWERT 3324 niemals
// und liefert dabei keinen Fehler, sondern ein leeres Ergebnis — die Stoerung
// bliebe still bestehen (ausfuehrlich im Kopfkommentar von
// `resolveLegacyPrinter`, firebase/functions/src/index.ts:1458-1464). Deshalb
// schreibt diese Datei ausschliesslich getrimmte Zeichenketten.
//
// Reine Funktionen ohne Vue-/Firestore-Abhaengigkeit, damit sie ohne Mount und
// ohne Netz pruefbar sind (dashboard/test/payfixPrinterId.test.ts).
// ─────────────────────────────────────────────────────────────────────────────

/** Ein Dokument oder Formularwert, aus dem die Kennung gelesen werden kann. */
export interface PayfixPrinterIdSource {
  /** Kanonische Quelle (REQ-DM-035). */
  payfixPrinterId?: unknown;
  /** Spiegel und Rueckfall fuer Bestandsdokumente (REQ-DM-036 (2)). */
  number?: unknown;
}

/**
 * Bringt einen beliebigen Rohwert in die kanonische Form: getrimmte
 * Zeichenkette. `null`/`undefined` und reine Leerzeichen ergeben `''`.
 *
 * Zahlen werden ausdruecklich mitgenommen (`3324` -> `'3324'`): Bestands- und
 * REST-Dokumente koennen `number` als Zahl fuehren, und der Alt-Eingang
 * normalisiert seinerseits mit `String(...)`.
 */
export function normalizePayfixPrinterId(raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw).trim() : '';
  return '';
}

/**
 * Leseprioritaet `payfixPrinterId ?? number` (REQ-DM-036 (2)).
 *
 * Der Rueckfall ist keine Nachlaessigkeit, er deckt genau zwei belegte Faelle:
 * Bestandsdokumente vor dem Nachtrag (REQ-OPS-040) und Drucker aus der REST-API
 * `POST /v1/printers`, die `number` gar nicht schreibt — fuer sie zeigte die
 * Liste bisher „—".
 *
 * @returns die Kennung als getrimmte Zeichenkette, oder `''` wenn keine da ist.
 */
export function readPayfixPrinterId(
  source: PayfixPrinterIdSource | null | undefined,
): string {
  if (!source) return '';
  const canonical = normalizePayfixPrinterId(source.payfixPrinterId);
  if (canonical) return canonical;
  return normalizePayfixPrinterId(source.number);
}

/**
 * Die beim Speichern zu schreibenden Felder aus dem EINEN Eingabefeld
 * (REQ-DM-036 (1)): kanonisch `payfixPrinterId`, gespiegelt `number`.
 *
 * Beide tragen denselben, getrimmten Zeichenketten-Wert. Ein leerer Wert wird
 * hier nicht abgefangen — dafuer ist die Pflichtfeldpruefung zustaendig
 * (`utils/printerValidation.ts`); diese Funktion beschreibt nur die Form.
 */
export function payfixPrinterIdWriteFields(raw: unknown): {
  payfixPrinterId: string;
  number: string;
} {
  const value = normalizePayfixPrinterId(raw);
  return { payfixPrinterId: value, number: value };
}

/**
 * i18n-SCHLUESSEL der Kollisionsmeldung (REQ-DM-036 (3), REQ-OV-002). Nie
 * fertiger Text — die Anzeige uebersetzt ueber vue-i18n und fuellt die Parameter
 * `{id}` (die belegte Kennung), `{name}` (der belegende Drucker) und seit
 * T-M29.4 `{company}` (der Mandant des belegenden Druckers).
 *
 * ⚠ DIESER SCHLUESSEL GILT SEIT T-M29.4 NUR NOCH FUER FALL (a) — den belegenden
 * Drucker IM GEWAEHLTEN MANDANTEN. Die zwei uebrigen Faelle tragen einen
 * eigenen Schluessel (siehe unten); welcher gilt, entscheidet
 * `payfixPrinterIdConflictMessage` und nicht die Anzeige.
 */
export const PAYFIX_PRINTER_ID_TAKEN_KEY = 'printers.validation.payfixPrinterIdTaken';

/**
 * Fall (b): der belegende Drucker liegt in einem FREMDEN Mandanten
 * (REQ-DM-036 Praez. 1). Der Bediener erfaehrt, dass er diesen Drucker nicht
 * sehen kann und wen er zu fragen hat — mehr nicht: aus der Meldung entsteht
 * ausdruecklich KEIN Weg, den fremden Drucker zu oeffnen, zu aendern oder zu
 * loeschen (REQ-SEC-011).
 */
export const PAYFIX_PRINTER_ID_TAKEN_OTHER_COMPANY_KEY =
  'printers.validation.payfixPrinterIdTakenOtherCompany';

/**
 * Fall (b′): der belegende Drucker liegt in einem fremden Mandanten, und der
 * Betrachter darf ihn NICHT sehen (REQ-DM-036 Praez. 3 (i), NEU mit T-M30.11).
 *
 * ⚠ DIESER FALL IST DER GRUND DER GANZEN PRAEZISIERUNG. Mit der
 * Mandantentrennung (REQ-SEC-011) ist ein fremdes `companies`-Dokument fuer
 * einen `user` nicht mehr lesbar — bis zum 2026-09-01 waere die Meldung damit
 * SYSTEMATISCH in Fall (c) gefallen. Ihr Wortlaut bliebe wahr („keiner lesbaren
 * Firma zugeordnet"), **ihr Handlungssatz nicht**: die Aufforderung, die
 * verwaiste Kennung dem Betrieb zu melden, waere an jedem voellig regulaeren
 * fremden Mandanten eine Falschmeldung, und der Bediener verloere genau die
 * Auskunft, um derentwillen Praez. 1 geschrieben ist — er erfuehre nicht mehr,
 * WEN er fragen muss (REQ-DM-036 Vermerk 1 zu Praez. 1 (b)).
 *
 * Deshalb: eigener Text, eigener Handlungssatz (Payfix-Support), und **weder
 * Firmenname noch Firmenkennung noch Druckername** — der Server liefert sie in
 * diesem Fall gar nicht erst (REQ-SEC-034 (c), Schluesselmenge `{status}`).
 */
export const PAYFIX_PRINTER_ID_TAKEN_OTHER_COMPANY_UNNAMED_KEY =
  'printers.validation.payfixPrinterIdTakenOtherCompanyUnnamed';

/**
 * Fall (c): VERWAISTE KENNUNG — der belegende Drucker ist keinem lesbaren
 * Mandanten zugeordnet (REQ-DM-036 Praez. 1 und Praez. 2).
 *
 * ZWEI URSACHEN, EIN FALL (Praez. 2 (i)): das `companies`-Dokument fehlt, ODER
 * der Drucker traegt gar keine `companyId`. Der Zustand ist derselbe — eine
 * belegte Kennung ohne erreichbaren Eigentuemer —, und der Bediener kann in
 * beiden Lagen dasselbe tun; eine Unterscheidung, die keine Handlung
 * unterscheidet, waere eine Beschriftung ohne Zweck.
 *
 * ⚠ DER WORTLAUT BEHAUPTET KEINE LOESCHUNG (Praez. 2 (ii)). „Der Mandant
 * besteht nicht mehr" waere bei einem Drucker ohne `companyId` unbelegt — dort
 * wurde nichts geloescht, dem Dokument fehlt die Zuordnung. Genannt wird der
 * ZUSTAND, nicht die vermutete Ursache. Die Wurzel des Falls ist REQ-DM-069
 * (Mandantenloeschung ohne Verwaisung); sie ist hier BENANNT und ausdruecklich
 * NICHT behoben (OP-178).
 */
export const PAYFIX_PRINTER_ID_TAKEN_ORPHANED_KEY =
  'printers.validation.payfixPrinterIdTakenOrphaned';

/**
 * DERSELBE FALL (c) — nur ohne die Nennung des belegenden Druckers
 * (T-M30.11).
 *
 * ⚠ **DAS IST KEIN FUENFTER FALL, SONDERN DERSELBE MIT WENIGER ANGABEN**:
 * gleicher Zustand („verwaiste Kennung"), gleicher Handlungssatz („dem Betrieb
 * melden"), gleiche Fall-Kennzeichnung `orphaned`. Er entsteht, weil der Server
 * den Druckernamen ohne Nenn-Berechtigung nicht ausliefert (REQ-SEC-034 (c)) —
 * die Schluesselmenge der Antwort ist dann `{status}`, und `«{name}»` haette
 * nichts einzusetzen.
 *
 * ⚠ **DIE ALTERNATIVEN SIND GEPRUEFT UND VERWORFEN.** Den Namen aus (c)
 * herausnehmen hiesse den Wortlaut aendern, den REQ-DM-036 Praez. 3 (i) (c)
 * ausdruecklich UNVERAENDERT laesst. Eine leere Nennung `«»` auszugeben waere
 * ein sichtbarer Mangel. Den Namen ersatzweise aus der Kennung zu bilden ginge
 * nicht — sie verlaesst den Server ebenfalls nicht.
 */
export const PAYFIX_PRINTER_ID_TAKEN_ORPHANED_UNNAMED_KEY =
  'printers.validation.payfixPrinterIdTakenOrphanedUnnamed';

/**
 * Die Kennung konnte NICHT geprueft werden (T-M30.11).
 *
 * ⚠ **SIE IST KEINE KOLLISIONSMELDUNG, SONDERN IHR AUSFALL** — und sie ist
 * noetig geworden, weil die Pruefung ihren Ort gewechselt hat: bis zum
 * 2026-09-01 war sie eine Firestore-Abfrage der laufenden Sitzung, ab jetzt ist
 * sie ein Aufruf an die Cloud (REQ-SEC-034). Ein Aufruf, der nicht ankommt,
 * darf **nicht** als „frei" durchgehen — das liesse genau die Dublette
 * entstehen, gegen die die Pruefung besteht (REQ-DM-035) — und er darf auch
 * nicht stumm bleiben: der Bediener klickte sonst auf Speichern und nichts
 * geschaehe.
 */
export const PAYFIX_PRINTER_ID_CHECK_FAILED_KEY =
  'printers.validation.payfixPrinterIdCheckFailed';

// ─────────────────────────────────────────────────────────────────────────────
// DIE KENNUNG ALS DOKUMENT-KENNUNG DES SPERRDOKUMENTS (T-M41.8, 2026-09-18)
//
// REQ-DM-036 Praez. 4 (viii)/(ix), REQ-OV-008 Praez. 2, REQ-DM-035,
// REQ-OPS-038 (EINE Quelle der Kanonisierung).
//
// Die Eindeutigkeit von `payfixPrinterId` traegt seit dem 2026-09-18 ein
// Sperrdokument `printerKeys/{payfixPrinterId}` — die DOKUMENT-ID traegt sie,
// und das gilt ohne jede Abfrage (OP-125). Daraus folgt eine Pruefung, die es
// vorher nicht brauchte: **nicht jede zulaessige Zeichenkette ist eine
// zulaessige Dokument-Kennung.** Firestore verbietet den Schraegstrich, die
// Namen `.` und `..`, die Form `__…__` und mehr als 1500 Byte.
//
// ⚠ EINE SOLCHE KENNUNG WIRD ABGEWIESEN UND NICHT STILL UEBERGANGEN. Wer sie
// durchliesse, haette genau zwei Ausgaenge: der Schreibvorgang scheitert an der
// Dokument-Kennung (und der Bediener liest einen technischen Fehler), oder das
// Sperrdokument entfaellt und der Drucker entsteht OHNE die Garantie, die diese
// Sammlung verspricht. Die Cloud antwortet an derselben Stelle mit 400
// (REQ-DM-036 Praez. 4 (ix)); die Oberflaeche weist es als Formularfehler ab —
// gleiche Regel, gleicher Anlass, anderer Traeger.
//
// ⚠ DIE KANONISIERUNG IST DIESELBE WIE UEBERALL: `String(wert).trim()` ueber
// `normalizePayfixPrinterId` (REQ-DM-035, `resolveLegacyPrinter`,
// `sperrdokumentSchluessel` in `firebase/functions/src/index.ts`). Sie wird hier
// NICHT ein zweites Mal gebaut (REQ-OPS-038).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * i18n-Schluessel der unzulaessigen Dokument-Kennung (REQ-DM-036 Praez. 4 (ix)).
 *
 * ⚠ FUER DIE LEERE KENNUNG GILT ER NICHT — sie ist kein Sonderfall dieser
 * Regel, sondern der bestehende Pflichtfeldfehler `numberRequired`; zwei
 * Meldungen fuer dieselbe Lage waeren eine Meldung zu viel.
 */
export const PAYFIX_PRINTER_ID_INVALID_KEY = 'printers.validation.payfixPrinterIdInvalid';

/** i18n-Schluessel der fehlenden Kennung — der bestehende Pflichtfeldfehler. */
export const PAYFIX_PRINTER_ID_REQUIRED_KEY = 'printers.validation.numberRequired';

/** Ergebnis von {@link payfixPrinterIdDocKey}: entweder die Kennung oder ein Grund. */
export type PayfixPrinterIdDocKey =
  | { key: string }
  | { error: typeof PAYFIX_PRINTER_ID_INVALID_KEY | typeof PAYFIX_PRINTER_ID_REQUIRED_KEY };

/** Die Byte-Laenge einer Zeichenkette in UTF-8 — Firestore zaehlt Bytes, nicht Zeichen. */
function byteLaenge(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * Kanonisiert die Kennung ZUR DOKUMENT-KENNUNG des Sperrdokuments und prueft,
 * ob sie ueberhaupt eine sein kann (REQ-DM-036 Praez. 4 (ix)).
 *
 * @returns `{ key }` mit der getrimmten Zeichenkette, oder `{ error }` mit dem
 *          i18n-Schluessel des Grundes. **Nie fertiger Text** (REQ-OV-002).
 */
export function payfixPrinterIdDocKey(raw: unknown): PayfixPrinterIdDocKey {
  const key = normalizePayfixPrinterId(raw);
  if (!key) return { error: PAYFIX_PRINTER_ID_REQUIRED_KEY };
  if (
    key.includes('/')
    || key === '.'
    || key === '..'
    || /^__.*__$/.test(key)
    || byteLaenge(key) > 1500
  ) {
    return { error: PAYFIX_PRINTER_ID_INVALID_KEY };
  }
  return { key };
}

/** Ein Drucker, der eine Kennung belegen kann. */
export interface PayfixPrinterIdCandidate extends PayfixPrinterIdSource {
  id: string;
  name?: string;
  /** Mandant des Druckers — am Dokument NICHT zugesichert (REQ-SEC-011). */
  companyId?: unknown;
}

/**
 * Sucht in einer Trefferliste den Drucker, der die Kennung bereits belegt
 * (REQ-DM-036 (3)).
 *
 * Verglichen wird ausschliesslich die KANONISCHE Quelle `payfixPrinterId` —
 * bewusst NICHT der Spiegel `number`. Der Alt-Eingang loest allein ueber
 * `payfixPrinterId` auf; ein Bestandsdokument, das nur `number` traegt, belegt
 * die Kennung fuer ihn also (noch) nicht. Genau diese Abfrageform hat auch die
 * REST-API mit ihrem 409.
 *
 * GRENZE, benannt statt behauptet: die Pruefung ist NICHT atomar (Abfrage und
 * Schreibvorgang sind zwei Schritte) und wird von den Firestore-Rules nicht
 * erzwungen — dasselbe gilt fuer den 409 der REST-API. Eine strukturelle
 * Garantie braeuchte eine eigene Schluessel-Collection mit Transaktion; sie ist
 * als OP-125 gefuehrt und nicht Teil dieses Hotfix. Was die Pruefung leistet,
 * ist der Fall, der real vorkommt: zwei Bediener, die dieselbe Portal-Kennung
 * an zwei Kanaelen erfassen.
 *
 * @param value      die einzutragende Kennung (wird normalisiert)
 * @param candidates Treffer der Abfrage auf `payfixPrinterId`
 * @param ownId      Dokument-ID des gerade bearbeiteten Druckers (ausgeschlossen)
 * @returns der belegende Drucker, oder `null` wenn die Kennung frei ist
 */
export function findPayfixPrinterIdConflict<T extends PayfixPrinterIdCandidate>(
  value: unknown,
  candidates: readonly T[] | null | undefined,
  ownId?: string | null,
): T | null {
  const wanted = normalizePayfixPrinterId(value);
  if (!wanted || !candidates) return null;
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (ownId && candidate.id === ownId) continue;
    if (normalizePayfixPrinterId(candidate.payfixPrinterId) === wanted) return candidate;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// DIE MELDUNG NENNT DIE FIRMA — UND DEN HERRENLOSEN FALL BEIM NAMEN (T-M29.4)
//
// REQ-DM-036 Praez. 1 (die drei Faelle) und Praez. 2 (der Drucker ganz ohne
// `companyId`), REQ-DM-035, REQ-DM-069 (nur benannt), REQ-SEC-011.
//
// WARUM DAS UEBERHAUPT NOETIG IST — der Fall ist gemeldet, nicht ausgedacht:
// die Kollisionspruefung ist SYSTEMWEIT und ohne Mandantenfilter (sie muss es
// sein, REQ-DM-035). Der Bediener las am 2026-08-24 „Die Printer ID 3028 ist
// bereits am Drucker «x» vergeben", fand diesen Drucker in seiner eigenen Firma
// aber nicht — und hatte keinen Weg, ihn zu suchen. Die Auskunft wird deshalb
// genauer; DIE GRENZE BLEIBT, WO SIE IST (REQ-SEC-011): die Meldung informiert,
// sie handelt nicht.
// ─────────────────────────────────────────────────────────────────────────────

/** Getrimmte Zeichenkette aus einem Rohwert; jeder Fremdtyp ergibt `''`. */
function trimmedText(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * Der Mandant des belegenden Druckers, so wie er gelesen werden konnte.
 * `null`/`undefined` an dieser Stelle heisst „nicht lesbar" — Dokument fehlt
 * oder es wurde gar nicht erst gelesen (Praez. 2 (iii)).
 */
export interface PayfixPrinterIdOwnerCompany {
  id?: unknown;
  name?: unknown;
}

/**
 * Die Faelle der Kollisionsmeldung.
 *
 * ⚠ **BIS ZUM 2026-09-01 WAREN ES DREI, SEIT T-M30.11 SIND ES VIER** —
 * berichtigt statt geloescht (REQ-OPS-036 Praez. 6). Der frueher hier stehende
 * Satz lautete: „Die drei Faelle aus REQ-DM-036 Praez. 1 — es gibt keinen
 * vierten." **Er galt fuer Praez. 1 und 2 und gilt seit Praez. 3 nicht mehr:**
 * der vierte Fall ist `otherCompanyUnnamed` — fremd und fuer den Betrachter
 * NICHT lesbar (REQ-DM-036 Praez. 3 (i) (b′)).
 *
 * ⚠ **`orphaned` HAT ZWEI WORTLAUTE UND BLEIBT TROTZDEM EIN FALL**: mit und
 * ohne Nennung des belegenden Druckers — gleicher Zustand, gleicher
 * Handlungssatz (siehe `PAYFIX_PRINTER_ID_TAKEN_ORPHANED_UNNAMED_KEY`).
 */
export type PayfixPrinterIdConflictCase =
  'ownCompany' | 'otherCompany' | 'otherCompanyUnnamed' | 'orphaned';

/** Was die Anzeige braucht: EIN Schluessel, seine Parameter, und welcher Fall. */
export interface PayfixPrinterIdConflictMessage {
  key: string;
  params: Record<string, string>;
  fall: PayfixPrinterIdConflictCase;
}

/**
 * Die Mandanten-Kennung, mit der nachgelesen werden DARF (REQ-DM-036 Praez. 2
 * (iii)).
 *
 * ⚠ OHNE KENNUNG WIRD NICHT GELESEN. Fehlt `companyId` oder ist sie leer,
 * liefert diese Funktion `''` — und der Aufrufer laesst den zusaetzlichen
 * Lesevorgang ersatzlos entfallen, statt auf `companies/undefined` zuzugreifen.
 * Ein Lesevorgang mit leerer Kennung ist kein „Dokument fehlt", sondern ein
 * Fehler; und ein Fehler an dieser Stelle verschluckte genau die Meldung, um
 * derentwillen das Ganze gebaut ist.
 */
export function payfixPrinterIdOwnerCompanyId(
  conflict: { companyId?: unknown } | null | undefined,
): string {
  return conflict ? trimmedText(conflict.companyId) : '';
}

/**
 * Waehlt Schluessel und Parameter der Kollisionsmeldung (REQ-DM-036 Praez. 1).
 *
 * ⚠ **SEIT DEM 2026-09-01 (T-M30.11) OHNE AUFRUFER IM PRODUKTIVCODE** —
 * berichtigt statt geloescht (REQ-OPS-036 Praez. 6). Sie war die
 * CLIENTSEITIGE Fallentscheidung: sie leitete „eigener gegen fremder Mandant"
 * aus `currentCompanyId` und einem selbst gelesenen `companies`-Dokument ab.
 * **Genau das darf die Oberflaeche nicht mehr** (REQ-DM-036 Praez. 3 (iii));
 * die Auskunft kommt vom Server, und die geltende Zuordnung trifft
 * {@link payfixPrinterIdMeldung}.
 *
 * **SIE WIRD TROTZDEM NICHT ZURUECKGEBAUT.** Ob eine Rechnung ohne Aufrufer
 * entfaellt, ist ein Entscheid des Ausgangstors und kein Nebeneffekt eines
 * Umsetzers — so entschieden am 2026-08-22 zu `readFastlineMode`
 * (T-M25.4 Umfang (5a)/(6)). Der Bestand ist hier gemeldet statt still
 * geheilt; sie ist damit Aufraeum-Kandidat der Klasse OP-59. **Wer sie ruft,
 * baut die verbotene Erhebung zurueck.**
 *
 * Der Firmenname wird mit NAMEN genannt, wenn er lesbar ist, sonst mit der
 * Mandanten-Kennung — dieselbe Regel wie bei der Drucker-Bezeichnung, die auf
 * die Dokument-ID zurueckfaellt.
 *
 * @param value            die einzutragende Kennung (wird normalisiert)
 * @param conflict         der belegende Drucker aus `findPayfixPrinterIdConflict`
 * @param ownerCompany     sein Mandant, oder `null` wenn nicht lesbar
 * @param currentCompanyId der hier gewaehlte Mandant
 */
export function payfixPrinterIdConflictMessage(input: {
  value: unknown;
  conflict: PayfixPrinterIdCandidate;
  ownerCompany?: PayfixPrinterIdOwnerCompany | null;
  currentCompanyId?: unknown;
}): PayfixPrinterIdConflictMessage {
  const id   = normalizePayfixPrinterId(input.value);
  const name = trimmedText(input.conflict?.name) || trimmedText(input.conflict?.id);

  const ownerId = payfixPrinterIdOwnerCompanyId(input.conflict);
  // Fall (c), erste Ursache: der Drucker traegt keine Zuordnung. Zweite
  // Ursache: das Dokument war nicht lesbar. BEIDE fuehren hierher (Praez. 2 (i)).
  const company = ownerId && input.ownerCompany
    ? trimmedText(input.ownerCompany.name) || trimmedText(input.ownerCompany.id) || ownerId
    : '';

  if (!company) {
    return { key: PAYFIX_PRINTER_ID_TAKEN_ORPHANED_KEY, params: { id, name }, fall: 'orphaned' };
  }

  // (a) derselbe Mandant — die Firma wird genannt wie in den anderen Faellen,
  // ohne Sonderweg; (b) ein fremder — derselbe Aufbau, ein anderer Satz.
  const fremd = ownerId !== trimmedText(input.currentCompanyId);
  return {
    key:    fremd ? PAYFIX_PRINTER_ID_TAKEN_OTHER_COMPANY_KEY : PAYFIX_PRINTER_ID_TAKEN_KEY,
    params: { id, name, company },
    fall:   fremd ? 'otherCompany' : 'ownCompany',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DIE MELDUNG KENNT VIER FAELLE STATT DREI — UND SIE ERHEBT SIE NICHT MEHR
// SELBST (T-M30.11)
//
// REQ-DM-036 Praez. 3 (die vier Faelle und die Bezugsquelle), Praez. 1 und 2
// (unveraendert in Kraft), REQ-SEC-034 (die Auskunft der Cloud), REQ-SEC-011.
//
// ── DIE EINE REGEL, DIE MAN KENNEN MUSS ────────────────────────────────────
//   **DIE VIER STATUSWERTE DES SERVERS SIND NICHT DIE VIER MELDUNGEN.**
//   `foreignCompany` traegt ZWEI davon, und die Unterscheidung liegt in der
//   SCHLUESSELMENGE der Antwort, nicht im Status:
//
//     ownCompany                          -> (a)  eigener Mandant
//     foreignCompany  + Firmenangabe      -> (b)  fremd und lesbar (Admin)
//     foreignCompany  ohne jedes Feld     -> (b') fremd und NICHT lesbar
//     orphaned                            -> (c)  verwaiste Kennung
//
//   **Wer allein auf `status` schaut, baut aus vier Meldungen wieder drei**
//   (Vollzugsvermerk zu REQ-SEC-034 vom 2026-09-01) — und laesst damit (b)
//   stillschweigend in (c) laufen, was REQ-DM-036 Vermerk 1 ausdruecklich
//   verbietet.
//
// ── WORAN DIE ZUORDNUNG GEMESSEN WIRD ──────────────────────────────────────
// An den VERWENDBAREN Angaben, nicht am blossen Vorhandensein eines
// Schluessels: `printerName` und `companyName` duerfen `null` sein (das
// Dokument fuehrt das Feld nicht), und dann faellt die Anzeige auf die KENNUNG
// zurueck — wie Praez. 1 es ohnehin verlangt. Erst wenn auch die fehlt, ist
// nichts zu nennen, und der Fall traegt seinen namenlosen Wortlaut.
//
// ── DIE GRENZE, unveraendert ───────────────────────────────────────────────
// DIE MELDUNG INFORMIERT, SIE HANDELT NICHT (REQ-SEC-011): aus ihr entsteht
// kein Weg, den fremden Drucker zu oeffnen, zu aendern oder zu loeschen — und
// im Fall (b') nicht einmal ein Name, den man suchen koennte.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Die Antwort der Cloud, so weit diese Rechnung sie braucht.
 *
 * Bewusst ein eigener, MINIMALER Typ und nicht der Rueckgabetyp des Aufrufs:
 * diese Datei bleibt damit rein — ohne Firebase-Weg, ohne Netz, ohne Mount
 * pruefbar. Die echte `PayfixPrinterIdAuskunft` aus
 * `firebase/payfixPrinterIdApi.ts` erfuellt ihn strukturell.
 */
export interface PayfixPrinterIdAuskunftSicht {
  status: 'free' | 'ownCompany' | 'foreignCompany' | 'orphaned';
  printerId?: unknown;
  printerName?: unknown;
  companyId?: unknown;
  companyName?: unknown;
}

/**
 * Ordnet der Antwort des Servers ihre Meldung zu (REQ-DM-036 Praez. 3).
 *
 * @returns `null`, wenn die Kennung frei ist — dann gibt es nichts zu melden
 *          und der Speicherweg bleibt offen.
 */
export function payfixPrinterIdMeldung(input: {
  value: unknown;
  auskunft: PayfixPrinterIdAuskunftSicht | null | undefined;
}): PayfixPrinterIdConflictMessage | null {
  const auskunft = input.auskunft;
  if (!auskunft || auskunft.status === 'free') return null;

  const id = normalizePayfixPrinterId(input.value);

  // Die zwei Rueckfallketten. Sie sind die einzige Stelle, an der entschieden
  // wird, ob etwas zu NENNEN ist — beide Namen duerfen `null` sein, beide
  // Kennungen koennen fehlen (REQ-SEC-034, dritter Randfall).
  const name    = trimmedText(auskunft.printerName) || trimmedText(auskunft.printerId);
  const company = trimmedText(auskunft.companyName) || trimmedText(auskunft.companyId);

  // (c) VERWAISTE KENNUNG — zuerst, weil sie vom Status allein entschieden
  // wird: „belegt, und der belegende Drucker ist keiner lesbaren Firma
  // zugeordnet". Der Wortlaut behauptet in beiden Fassungen KEINE Loeschung
  // (Praez. 2 (ii)); ohne Nenn-Berechtigung entfaellt allein die Nennung des
  // Druckers, nicht der Handlungssatz.
  if (auskunft.status === 'orphaned') {
    return name
      ? { key: PAYFIX_PRINTER_ID_TAKEN_ORPHANED_KEY, params: { id, name }, fall: 'orphaned' }
      : { key: PAYFIX_PRINTER_ID_TAKEN_ORPHANED_UNNAMED_KEY, params: { id }, fall: 'orphaned' };
  }

  // (b') FREMD UND NICHT LESBAR — der Server hat nichts mitgeschickt, was zu
  // nennen waere. **Die Antwort entscheidet, nicht der Text**: hier stehen
  // weder Firmenname noch Firmenkennung noch Druckername zur Verfuegung, und
  // deshalb kann keiner von ihnen in die Meldung geraten.
  if (auskunft.status === 'foreignCompany' && !company) {
    return {
      key:    PAYFIX_PRINTER_ID_TAKEN_OTHER_COMPANY_UNNAMED_KEY,
      params: { id },
      fall:   'otherCompanyUnnamed',
    };
  }

  // (a) eigener Mandant und (b) fremd UND lesbar — derselbe Aufbau, ein
  // anderer Satz. Beide nennen Kennung, belegenden Drucker und Firma.
  const fremd = auskunft.status === 'foreignCompany';
  return {
    key:    fremd ? PAYFIX_PRINTER_ID_TAKEN_OTHER_COMPANY_KEY : PAYFIX_PRINTER_ID_TAKEN_KEY,
    params: { id, name, company },
    fall:   fremd ? 'otherCompany' : 'ownCompany',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DIE KOLLISION AUS DER TRANSAKTION — DIESELBEN VIER MELDUNGEN, EINE ZWEITE
// BEZUGSQUELLE (T-M41.8, 2026-09-18)
//
// REQ-DM-036 Praez. 1 und Praez. 4 (c)/(f), REQ-SEC-034 (c), REQ-OPS-038.
//
// ── WARUM ES DIESE STELLE UEBERHAUPT GIBT ──────────────────────────────────
// Bis zum 2026-09-18 gab es genau EINE Lage, in der eine Kollision sichtbar
// wurde: die Vorab-Auskunft `checkpayfixprinterid` meldete sie (REQ-SEC-034).
// Sie ist NICHT atomar — zwischen Auskunft und Schreibvorgang liegt ein
// Fenster (REQ-SEC-034 (f), OP-125). Seit dem Sperrdokument faellt die zweite
// Lage an: die Auskunft sagte „frei", und die TRANSAKTION findet die Kennung
// belegt. **Der Bediener muss in beiden Lagen denselben Satz lesen** — es ist
// dieselbe Tatsache, nur eine Sekunde spaeter.
//
// ── ES ENTSTEHT KEINE FUENFTE MELDUNG UND KEIN ZWEITER WORTLAUT ────────────
// Diese Rechnung bildet aus dem Sperrdokument eine Antwort in der Gestalt der
// Auskunft und laesst {@link payfixPrinterIdMeldung} entscheiden. Die Zuordnung
// „Antwort -> Meldung" bleibt damit an EINER Stelle (REQ-OPS-038); wer sie
// aendert, aendert beide Wege zugleich.
//
// ── DIE ZWEI FAELLE, UND WARUM SIE GENAU SO ZUGEORDNET SIND ────────────────
//  (1) DAS SPERRDOKUMENT WAR LESBAR. Die Rules lassen es nur lesen, wer es
//      nennen darf: der `admin` oder ein Mitglied des Mandanten, dem es gehoert
//      (REQ-SEC-034 (c)). Was wir dann haben, sind die zwei KENNUNGEN aus dem
//      Dokument (`printerId`, `companyId`) — keine Namen. **Das ist kein
//      Mangel, sondern der bestehende Rueckfall:** die Meldung nennt „mit Namen,
//      wenn er lesbar ist, sonst mit seiner Kennung" (Praez. 1).
//  (2) DAS SPERRDOKUMENT WAR NICHT LESBAR (`permission-denied`). Genau dann
//      gehoert es einem fremden Mandanten — die Rules weisen diesen Lesevorgang
//      ausdruecklich ab (REQ-SEC-034 (c)). Das IST der Fall (b′): „in einer
//      anderen Firma vergeben", ohne Nennung, mit dem Verweis auf den
//      Payfix-Support. **Ein anderer Wortlaut waere hier eine Behauptung ueber
//      eine Ursache, die wir nicht kennen** — insbesondere die „verwaiste
//      Kennung" (c), die eine Aussage ueber den EIGENTUEMER trifft und nicht
//      ueber unser Leserecht (REQ-DM-036 Praez. 2 (ii)).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Die Kennung ist belegt — geworfen INNERHALB der Schreib-Transaktion, damit
 * sie zurueckrollt (`services/printerKeyService.ts`), und von der Ansicht in
 * die bestehende Kollisionsmeldung uebersetzt.
 *
 * ⚠ **SIE LIEGT HIER UND NICHT BEIM DIENST, UND DAS IST EIN ENTSCHEID.** Die
 * Ansicht prueft sie mit `instanceof` — sie braucht die Klasse also zur
 * LAUFZEIT. Laege sie im Dienst, zoege `PrinterDetailView.vue` ueber ihn
 * `@/firebase/config` herein, und **sieben bestehende Mount-Belegdateien
 * muessten eine Attrappe nachtragen, die mit der Sache nichts zu tun hat**
 * (gemessen am 2026-09-18 an `test/attrappenVollstaendigkeit.test.ts`). Dieses
 * Modul ist rein — ohne Vue, ohne Firestore, ohne Netz —, und die Kollision
 * ist ohnehin sein Gegenstand.
 *
 * `fremd` heisst: das Sperrdokument war **nicht lesbar** (`permission-denied`).
 * Die Rules lassen ein fremdes bestehendes Sperrdokument nicht lesen
 * (REQ-SEC-034 (c)) — dass die Lesung scheitert, sagt genau zweierlei: das
 * Dokument besteht, und es gehoert nicht uns. **Es ist kein Fehlerzustand,
 * sondern die Auskunft in der Form, in der sie zulaessig ist.**
 *
 * ⚠ DER PARAMETER HEISST `belegung` UND NICHT `opts`, und das ist kein
 * Geschmack: `opts`/`deps` sind in diesem Repo die Namen eines
 * INJEKTIONSCONTAINERS, und der Waechter der Nachweisluecken
 * (`firebase/functions/test/unit/nachweisluecken.test.ts`, REQ-OPS-036) erhebt
 * sie als Test-Naht. **Hier liegt keine Naht, sondern ein Wertobjekt** — ein
 * zutreffender Name spart der Registrierung dort einen Eintrag ueber eine
 * Nahtstelle, die es nicht gibt (gemessen am 2026-09-18: mit `opts` fielen drei
 * Fehltreffer an).
 */
export class PayfixPrinterIdBelegtError extends Error {
  readonly fremd: boolean;
  readonly belegenderDrucker: string;
  readonly belegenderMandant: string;
  constructor(belegung: {
    fremd: boolean;
    belegenderDrucker?: string;
    belegenderMandant?: string;
  }) {
    super(
      belegung.fremd
        ? 'payfixPrinterId belegt (Sperrdokument nicht lesbar)'
        : `payfixPrinterId belegt durch ${belegung.belegenderDrucker || 'unbekannt'}`,
    );
    this.name              = 'PayfixPrinterIdBelegtError';
    this.fremd             = belegung.fremd;
    this.belegenderDrucker = belegung.belegenderDrucker ?? '';
    this.belegenderMandant = belegung.belegenderMandant ?? '';
  }
}

/**
 * Bildet die Kollisionsmeldung aus dem gelesenen (oder eben NICHT lesbaren)
 * Sperrdokument.
 *
 * @param value             die einzutragende Kennung (wird normalisiert)
 * @param fremd             war das Sperrdokument NICHT lesbar? (`permission-denied`)
 * @param belegenderDrucker `printerId` aus dem Sperrdokument, wenn lesbar
 * @param belegenderMandant `companyId` aus dem Sperrdokument, wenn lesbar
 */
export function payfixPrinterIdMeldungAusSperrdokument(input: {
  value: unknown;
  fremd: boolean;
  belegenderDrucker?: unknown;
  belegenderMandant?: unknown;
}): PayfixPrinterIdConflictMessage {
  // Fall (2): nichts zu nennen — die Schluesselmenge ist genau `{status}`,
  // dieselbe Gestalt, die der Server im Fall (b′) sendet.
  if (input.fremd) {
    return payfixPrinterIdMeldung({
      value:    input.value,
      auskunft: { status: 'foreignCompany' },
    })!;
  }
  // Fall (1): die zwei Kennungen aus dem Dokument. `printerName`/`companyName`
  // gibt es hier nie — das Sperrdokument fuehrt sie nicht (REQ-DM-036
  // Praez. 4 (viii): die Feldmenge ist abschliessend), und die bestehende
  // Rueckfallkette setzt dann die Kennungen ein.
  //
  // ⚠ OHNE MANDANT IST ES FALL (c) UND NICHT FALL (a) — so ausdruecklich
  // REQ-DM-036 Praez. 2 (i): „Ein Drucker ohne `companyId` faellt in Fall (c);
  // der Zustand heisst dort verwaiste Kennung, und er ist es." Ein
  // Sperrdokument ohne lesbaren Mandanten kann der Bestandswanderung
  // entstammen (sie laeuft ueber das Admin SDK an den Rules vorbei,
  // REQ-OPS-040). Ohne diese Weiche stuende in der Meldung „der Firma «»".
  const mandant = trimmedText(input.belegenderMandant);
  return payfixPrinterIdMeldung({
    value:    input.value,
    auskunft: mandant
      ? { status: 'ownCompany', printerId: input.belegenderDrucker, companyId: mandant }
      : { status: 'orphaned',   printerId: input.belegenderDrucker },
  })!;
}
